import React, { useCallback, useMemo, useState } from "react";
import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifyError, notifySuccess } from "../../../utils/notify";
import {
  IconDeviceFloppy,
  IconEye,
  IconEyeOff,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import {
  FieldType,
  FormCategory,
  FormTemplate,
  FormTemplateField,
  ValidityDuration,
} from "../../../types/formTemplates";
import { createDefaultField, FIELD_TYPE_META } from "../constants";
import { FieldSidebar } from "./FieldSidebar";
import { FieldCanvas } from "./FieldCanvas";
import { FormSettingsModal } from "./FormSettingsModal";
import { TemplatePreview } from "../preview/TemplatePreview";
import {
  validateTemplate,
  groupErrorsByFieldId,
} from "../../../utils/formTemplateValidation";
import { useFormTemplates } from "../../../context/FormTemplatesContext";

export interface FormBuilderProps {
  mode: "create" | "edit";
  initialTemplate?: FormTemplate;
  cloneFromSlug?: string;
  /** Seed value for category when no initialTemplate is provided. Lets the
   * opener express intent (e.g. SOAP Notes tab → "per_visit_clinical") so
   * the user doesn't have to remember to open Form Settings and pick the
   * right radio. Ignored when ``initialTemplate`` is set — editing always
   * reflects the saved category. Defaults to ``"reusable"``. */
  defaultCategory?: FormCategory;
  onClose: () => void;
  onSaved: (template: FormTemplate) => void;
}

function newFieldId(): string {
  // `crypto.randomUUID` is available in all supported browsers + jsdom >= 20.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
  }
  return `field-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const FormBuilder: React.FC<FormBuilderProps> = ({
  mode,
  initialTemplate,
  cloneFromSlug,
  defaultCategory,
  onClose,
  onSaved,
}) => {
  const {
    createTemplateFromScratch,
    cloneTemplate,
    updateTemplate,
    deleteTemplate,
  } = useFormTemplates();

  const [name, setName] = useState<string>(
    initialTemplate?.name
      ? mode === "create" && cloneFromSlug
        ? `${initialTemplate.name} (Copy)`
        : initialTemplate.name
      : "Untitled Form",
  );
  const [description, setDescription] = useState<string | null>(
    initialTemplate?.description ?? null,
  );
  /** Category encodes usage intent (reusable / per_visit / per_visit_clinical).
   * Persisted on save and editable via FormSettingsModal — does not change
   * the builder's field-editing behavior. Seeded from the existing template
   * (edit) or from the caller-provided ``defaultCategory`` (create). Falling
   * back to ``"reusable"`` only when the caller didn't express an intent. */
  const [category, setCategory] = useState<FormCategory>(
    initialTemplate?.category ?? defaultCategory ?? "reusable",
  );
  const [fields, setFields] = useState<FormTemplateField[]>(
    initialTemplate?.schema_json.fields.map((f) => ({ ...f })) ?? [],
  );
  const [validityDuration, setValidityDuration] =
    useState<ValidityDuration | null>(
      initialTemplate?.validity_duration ?? null,
    );
  const [isPreview, setIsPreview] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const validation = useMemo(
    () =>
      validateTemplate({
        name,
        category,
        schema: { fields },
      }),
    [name, category, fields],
  );
  const errorsByFieldId = useMemo(
    () => groupErrorsByFieldId(validation.errors),
    [validation.errors],
  );
  const globalErrors = validation.errors.filter((e) => !e.fieldId);

  const addField = useCallback((type: FieldType, insertAt?: number) => {
    setFields((prev) => {
      const next = createDefaultField(newFieldId(), type);
      if (typeof insertAt === "number") {
        const copy = [...prev];
        copy.splice(insertAt, 0, next);
        return copy;
      }
      return [...prev, next];
    });
  }, []);

  const updateField = useCallback((updated: FormTemplateField) => {
    setFields((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  }, []);

  const removeField = useCallback((id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const moveField = useCallback((id: string, direction: "up" | "down") => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === id);
      if (idx === -1) return prev;
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      return arrayMove(prev, idx, swap);
    });
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    // Palette → Canvas insert
    if (activeId.startsWith("sidebar:")) {
      const fieldType = activeId.slice("sidebar:".length) as FieldType;
      if (!FIELD_TYPE_META[fieldType]) return;
      if (overId === "canvas") {
        addField(fieldType);
      } else if (overId.startsWith("field:")) {
        const overFieldId = overId.slice("field:".length);
        const idx = fields.findIndex((f) => f.id === overFieldId);
        addField(fieldType, idx >= 0 ? idx : undefined);
      }
      return;
    }

    // Canvas reorder
    if (activeId.startsWith("field:") && overId.startsWith("field:")) {
      const fromId = activeId.slice("field:".length);
      const toId = overId.slice("field:".length);
      if (fromId === toId) return;
      setFields((prev) => {
        const fromIdx = prev.findIndex((f) => f.id === fromId);
        const toIdx = prev.findIndex((f) => f.id === toId);
        if (fromIdx < 0 || toIdx < 0) return prev;
        return arrayMove(prev, fromIdx, toIdx);
      });
    }
  };

  const handleSave = async () => {
    if (!validation.ok) {
      notifyError(validation.errors[0]?.message ?? "Template is invalid.", {
        title: "Fix validation errors",
      });
      return;
    }
    setIsSaving(true);
    try {
      let saved: FormTemplate;
      if (mode === "edit" && initialTemplate) {
        saved = await updateTemplate(initialTemplate.id, {
          name,
          description,
          category,
          schema_json: { fields },
          validity_duration: validityDuration,
        });
      } else if (mode === "create" && cloneFromSlug) {
        // Use the clone endpoint which copies name + schema from the standard.
        // The builder's local edits are applied as a follow-up PUT so the
        // user's customizations are persisted. If that PUT fails, roll back
        // the cloned template so the user isn't left with an unedited copy.
        const cloned = await cloneTemplate({
          clone_from_slug: cloneFromSlug,
          name,
          description,
          category,
        });
        try {
          saved = await updateTemplate(cloned.id, {
            name,
            description,
            category,
            schema_json: { fields },
            // ``validityDuration`` carries the operator's explicit choice
            // (null for "Indefinitely" / per_visit / SOAP). Previous
            // ``?? cloned.validity_duration`` fallback silently resurrected
            // the standard template's value, which violated the backend
            // invariant (per_visit + non-null = 400) and overwrote the
            // operator's "Indefinitely" choice with the standard's expiry.
            validity_duration: validityDuration,
          });
        } catch (err) {
          try {
            await deleteTemplate(cloned.id);
          } catch {
            /* best-effort rollback — surface the original error below */
          }
          throw err;
        }
      } else {
        saved = await createTemplateFromScratch({
          name,
          description,
          category,
          schema_json: { fields },
          validity_duration: validityDuration,
        });
      }
      notifySuccess(`${saved.name} is now at version ${saved.version}.`, {
        title: "Template saved",
      });
      onSaved(saved);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "Unexpected error saving template.",
        { title: "Could not save template" },
      );
    } finally {
      setIsSaving(false);
    }
  };

  const overlayMeta =
    activeDragId && activeDragId.startsWith("sidebar:")
      ? FIELD_TYPE_META[activeDragId.slice("sidebar:".length) as FieldType]
      : null;

  return (
    <Modal
      opened
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      styles={{ body: { padding: 0, height: "100dvh", overflow: "hidden" } }}
    >
      <Stack gap={0} style={{ height: "100dvh" }}>
        <Group
          justify="space-between"
          align="center"
          px="md"
          py="sm"
          style={{
            borderBottom: "1px solid var(--mantine-color-gray-2)",
            background: "var(--mantine-color-white)",
            flexShrink: 0,
          }}
        >
          <Group gap="md" align="center">
            <Group gap={6} align="center" wrap="nowrap">
              <TextInput
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                placeholder="Template name"
                size="md"
                styles={{ input: { fontWeight: 600, fontSize: 16 } }}
                w={320}
                aria-label="Template name"
              />
              <Tooltip label="Form settings">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={() => setIsSettingsOpen(true)}
                  aria-label="Form settings"
                >
                  <IconSettings size={20} />
                </ActionIcon>
              </Tooltip>
            </Group>
            {validation.errors.length > 0 && (
              <Badge color="red" variant="light">
                {validation.errors.length} issue
                {validation.errors.length === 1 ? "" : "s"}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Tooltip label="Close without saving">
              <Button
                variant="outline"
                color="red"
                leftSection={<IconX size={16} />}
                onClick={onClose}
              >
                Close
              </Button>
            </Tooltip>
            <Button
              variant="outline"
              leftSection={
                isPreview ? <IconEyeOff size={16} /> : <IconEye size={16} />
              }
              onClick={() => setIsPreview((v) => !v)}
            >
              {isPreview ? "Close Preview" : "Preview"}
            </Button>
            <Button
              color="var(--brand-teal)"
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              disabled={!validation.ok}
              loading={isSaving}
            >
              Save Template
            </Button>
          </Group>
        </Group>

        {globalErrors.length > 0 && (
          <Box
            px="md"
            py="xs"
            style={{
              background: "var(--mantine-color-red-0)",
              borderBottom: "1px solid var(--mantine-color-red-2)",
            }}
          >
            <Text c="red.8" size="sm">
              {globalErrors.map((e) => e.message).join(" • ")}
            </Text>
          </Box>
        )}

        {isPreview ? (
          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: 24,
              background: "var(--mantine-color-gray-0)",
            }}
          >
            <TemplatePreview
              name={name}
              description={description}
              schema={{ fields }}
            />
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
            <Group
              align="stretch"
              gap={0}
              wrap="nowrap"
              style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
            >
              <FieldSidebar onAddField={addField} />
              <FieldCanvas
                fields={fields}
                errorsByFieldId={errorsByFieldId}
                onUpdateField={updateField}
                onRemoveField={removeField}
                onMoveField={moveField}
              />
            </Group>
            <DragOverlay>
              {overlayMeta ? (
                <Box
                  p="xs"
                  style={{
                    background: "var(--mantine-color-white)",
                    border: "1px solid var(--mantine-color-teal-3)",
                    borderRadius: 6,
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  }}
                >
                  <Text size="sm">{overlayMeta.label}</Text>
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Stack>

      <FormSettingsModal
        opened={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        category={category}
        validityDuration={validityDuration}
        onSave={({ category: next_category, validity_duration }) => {
          setCategory(next_category);
          setValidityDuration(validity_duration);
        }}
      />
    </Modal>
  );
};
